import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  TimePicker,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { TimeLimit, WEEKDAY_OPTIONS } from "./types";
import { getStorePickup, upsertStorePickup } from "./api";
import { ExtraFieldsEditor } from "./ExtraFieldsEditor";

interface Props {
  sellerCode: string;
}

interface FormShape {
  enabled: boolean;
  title?: string;
  description?: string;
  minHoursDisplay?: number;
  maxDaysToDisplay?: number;
  maxSimultaneousToDisplay?: number;
  timeLimits: { day: number; from: dayjs.Dayjs; to: dayjs.Dayjs; position?: string }[];
  extraFields: { label: string; type: string; options?: string }[];
}

export const StorePickupCard: React.FC<Props> = ({ sellerCode }) => {
  const [form] = Form.useForm<FormShape>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getStorePickup(sellerCode);
      form.setFieldsValue({
        enabled: data.enabled,
        title: data.title,
        description: data.description,
        minHoursDisplay: data.minHoursDisplay,
        maxDaysToDisplay: data.maxDaysToDisplay,
        maxSimultaneousToDisplay: data.maxSimultaneousToDisplay,
        timeLimits: (data.timeLimits ?? []).map((tl) => ({
          day: tl.day,
          from: tl.from ? dayjs(tl.from, "HH:mm") : dayjs("09:00", "HH:mm"),
          to: tl.to ? dayjs(tl.to, "HH:mm") : dayjs("18:00", "HH:mm"),
          position: tl.position,
        })),
        extraFields: data.extraFields ?? [],
      });
    } catch (err: any) {
      message.error("No se pudo cargar Store Pickup");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sellerCode) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerCode]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const timeLimits: TimeLimit[] = (values.timeLimits ?? []).map((tl) => ({
        day: tl.day,
        from: tl.from?.format("HH:mm"),
        to: tl.to?.format("HH:mm"),
        position: tl.position,
      }));
      await upsertStorePickup(sellerCode, {
        enabled: values.enabled,
        title: values.title,
        description: values.description,
        minHoursDisplay: values.minHoursDisplay,
        maxDaysToDisplay: values.maxDaysToDisplay,
        maxSimultaneousToDisplay: values.maxSimultaneousToDisplay,
        timeLimits,
        // Magento dynamic_rows legacy data injects `record_id` and `initialize`
        // tracking fields into the persisted JSON. The API DTO uses
        // `forbidNonWhitelisted`, so strip everything that isn't part of
        // the schema before sending.
        extraFields: (values.extraFields ?? []).map(
          ({ label, type, options }: any) => ({ label, type, options }),
        ),
      });
      message.success("Store Pickup guardado");
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Error al guardar";
      message.error(Array.isArray(msg) ? msg.join(" • ") : msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title="Store Pickup (retiro en local)"
      extra={
        <Button type="primary" loading={saving} onClick={handleSave}>
          Guardar
        </Button>
      }
      style={{ marginBottom: 16 }}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Habilitado" name="enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item label="Título (opcional)" name="title">
                <Input placeholder="Retiro en local" />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item label="Descripción (opcional)" name="description">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Mín. horas para mostrar" name="minHoursDisplay">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Máx. días a mostrar" name="maxDaysToDisplay">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Máx. simultáneos por slot" name="maxSimultaneousToDisplay">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.List name="timeLimits">
            {(fields, { add, remove }) => (
              <div style={{ marginBottom: 16 }}>
                {fields.map((field) => (
                  <Row key={field.key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                    <Col span={7}>
                      <Form.Item
                        {...field}
                        name={[field.name, "day"]}
                        label={field.name === 0 ? "Día" : ""}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          options={WEEKDAY_OPTIONS}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item
                        {...field}
                        name={[field.name, "from"]}
                        label={field.name === 0 ? "Desde" : ""}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 0 }}
                      >
                        <TimePicker format="HH:mm" minuteStep={15} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item
                        {...field}
                        name={[field.name, "to"]}
                        label={field.name === 0 ? "Hasta" : ""}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 0 }}
                      >
                        <TimePicker format="HH:mm" minuteStep={15} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={3} style={{ display: "flex", alignItems: "center" }}>
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                        danger
                      />
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  onClick={() =>
                    add({ day: 1, from: dayjs("09:00", "HH:mm"), to: dayjs("18:00", "HH:mm") })
                  }
                  icon={<PlusOutlined />}
                  block
                >
                  Agregar franja horaria
                </Button>
              </div>
            )}
          </Form.List>

          <ExtraFieldsEditor name="extraFields" />
        </Form>
      </Spin>
    </Card>
  );
};
